import { AlertTriangle } from "lucide-react";

/**
 * Amber hint pill shown next to a chance label whenever the classifier
 * could not translate the applicant's curriculum into any curriculum
 * the university publishes (`classification.grade_incomparable === true`).
 *
 * Rendered on:
 *  - Advanced Search results (`ResultCard.jsx` inside `AdvancedResults`)
 *  - Admission Calculator result rows
 *  - Degree Detail chance banner
 *  - AI Professor narrative card (as a small pill next to the label)
 *
 * The pill closes the loop between the internal "-0.85 incomparable
 * penalty" and the user's visible label so the student understands WHY
 * a DD A-Level at a Korean uni gets 'Ambitious' instead of a real
 * numeric fit.
 */
export default function GradeConversionMissingBadge({
  compact = false,
  applicantCurriculum = null,
  className = "",
}) {
  const label = compact
    ? "Grade conversion unavailable"
    : `Grade conversion unavailable${applicantCurriculum ? ` for ${prettify(applicantCurriculum)}` : ""} — chance capped conservatively`;
  return (
    <span
      data-testid="grade-conversion-missing-badge"
      className={`inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 leading-tight ${className}`}
      title="We couldn't map your grade to the requirements this university publishes, so we treated the profile conservatively. Add a recognised equivalent (IB, GPA, SAT) or contact admissions for a firmer read."
    >
      <AlertTriangle className="h-3 w-3 shrink-0" />
      {label}
    </span>
  );
}

function prettify(curr) {
  const map = {
    a_level: "A-Level",
    ib: "IB",
    gpa: "GPA",
    cbse: "CBSE",
    isc: "ISC",
    atar: "ATAR",
    suneung: "Suneung",
    american_diploma: "US Diploma",
    ap: "AP",
    abitur: "Abitur",
    french_bac: "French Bac",
    hkdse: "HKDSE",
  };
  return map[curr] || curr;
}
